import { Button, ButtonGroup } from "@mui/material";
import { where } from "firebase/firestore";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useAuthContext } from "../Auth";
import { ProgressContainer } from "../components/UI/ProgressContainer";
import { TableBookings } from "../components/UI/TableBookings";
import { DialogBookingComment, IDialogType } from "../dialogs/DialogBookingComment";
import { ReservationStatus, RoomReservation } from "../ostertalertypes";
import { fetchAllocationBookings } from "../utils/database";
import { daysBetween } from "../utils/dateutils";
import { handleError } from "../utils/utils";
import { filterReservationsByAdminHouses } from "../utils/adminutils";


interface IPageMyBookings {
  viewType: ViewType;
}

export type ViewType = "mine" | "incoming";

const PageMyBookings: React.FC<IPageMyBookings> = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [reservations, setReservations] = useState<RoomReservation[]>([]);
  const [reload, setReload] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<RoomReservation | null>(null);
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogType, setDialogType] = useState<IDialogType | null>(null);
  const auth = useAuthContext();

  useEffect(() => {
    async function fetchBookings() {
      try {
        setIsLoading(true);
        let sortedAsc: RoomReservation[] = [];
        if (props.viewType === "mine" && auth.isLoggedIn()) {
          let myReservations = await fetchAllocationBookings(false, where("userid", "==", auth.user?.uid));
          sortedAsc = myReservations.sort(
            (objA, objB) => objB.from?.toMillis()! - objA.from?.toMillis()!,
          );

        } else if (auth.isAdmin()) { // incoming bookings for admin view
          let myReservations = await fetchAllocationBookings(true, where("status", "in", [ReservationStatus.BOOKING_REQUESTED, ReservationStatus.CANCEL_REQUESTED]));
          // Filter by admin's accessible houses
          const filteredReservations = filterReservationsByAdminHouses(myReservations, auth.adminHouses);
          sortedAsc = filteredReservations.sort((objA, objB) => objB.lastUpdate?.toMillis()! - objA.lastUpdate?.toMillis()!);
        }
        setReservations(sortedAsc);
        setIsLoading(false);
      } catch (error) {
        handleError("Fehler beim Abrufen der Reservierungen", error, enqueueSnackbar, setIsLoading);
      }
    }

    fetchBookings();
  }, [reload, auth, props.viewType, enqueueSnackbar]);


  function onDialogOpen(res: RoomReservation, dialog: IDialogType){
    setSelectedItem(res);
    setDialogType(dialog);
    setDialogOpen(true);
  }

  function onDialogClose(changed: boolean){
    setDialogOpen(false);
    setSelectedItem(null);
    if (changed) {
      setReload((oldVal) => !oldVal);
    }
  }

  function getActionBtn(res: RoomReservation) {
    if (props.viewType === "mine") { // my own bookings
      let disabled=false;
      if (res.from) {
        const daysbetween = daysBetween(new Date(), res.from.toMillis())
        if (daysbetween > 0) {
          disabled = true; // reservation is in the past
        }
      }
      if (res.status === ReservationStatus.ACCEPTED || res.status === ReservationStatus.BOOKING_REQUESTED) {
        const dialog: IDialogType = {textfields: [{value:"", disabled:false}],
          buttons:[{text:"Stornieren anfragen", action:ReservationStatus.CANCEL_REQUESTED, required:true}],
          caption:"Dein Stornierungsgrund"};
        return <ButtonGroup disabled={disabled} size="small" aria-label="small button group">
                <Button key="one" onClick={() => onDialogOpen(res, dialog)}>Stornieren</Button>
               </ButtonGroup>
      }
      else if (res.status === ReservationStatus.BOOKING_REJECTED) {
        const dialog: IDialogType = {textfields: [{value:res.adminComment?res.adminComment: "", disabled:true}],
          buttons:[{text:"Ok", action:null}],
          caption:"Ablehnungsgrund"};
        return <ButtonGroup disabled={disabled} size="small" aria-label="small button group">
                <Button key="one" onClick={() => onDialogOpen(res, dialog)}>Details</Button>
               </ButtonGroup>
      }
      else if (res.status === ReservationStatus.CANCEL_REJECTED) {
        const dialog: IDialogType = {textfields: [{value:res.userComment?res.userComment: "", disabled:true},
                                                  {value:res.adminComment?res.adminComment: "", disabled:true}],
                                                  buttons:[{text:"Ok", action:null}],
                                                  caption:"Ablehnungsgrund"};
        return <ButtonGroup disabled={disabled} size="small" aria-label="small button group">
                <Button key="one" onClick={() => onDialogOpen(res, dialog)}>
                    Details
                </Button>
               </ButtonGroup>
      }
    }
    else { // icoming bookings
      if (res.status === ReservationStatus.CANCEL_REQUESTED) {
        const dialog: IDialogType = {textfields: [{value:res.userComment?res.userComment: "", disabled:true, label:"Nachricht Gast"},
                                                  {value:res.adminComment?res.adminComment: "", disabled:false, label:"Grund für Ablehnung"}],
                                                  buttons:[{text:"Annehmen", action:ReservationStatus.CANCELED},
                                                           {text:"Ablehnen", action:ReservationStatus.CANCEL_REJECTED, required:true}],
                                                  caption:"Stornierungsgrund des Gastes"};
        return <ButtonGroup size="small" aria-label="small button group">
                <Button key="one" onClick={() => onDialogOpen(res, dialog)}>Details</Button>
               </ButtonGroup>
      }
      else if(res.status === ReservationStatus.BOOKING_REQUESTED) {
        const dialog: IDialogType = {textfields: [{value:res.userComment?res.userComment: "", disabled:true, label:"Nachricht Gast"},
                                                  {value:res.adminComment?res.adminComment: "", disabled:false, label:"Grund für Ablehnung"}],
                                                  buttons:[{text:"Annehmen", action:ReservationStatus.ACCEPTED},
                                                  {text:"Ablehnen", action:ReservationStatus.BOOKING_REJECTED, required:true}],
                                                  caption:"Buchungsanfrage"};
        return <ButtonGroup size="small" aria-label="small button group">
                <Button key="one" onClick={() => onDialogOpen(res, dialog)}>Details</Button>
               </ButtonGroup>
      }
    }

  }

  return (
    <ProgressContainer size="max-w-5xl" isLoading={isLoading}>
      <h1 className="text-2xl mt-0 mb-5 text-gray-500 text-center md:text-left">{props.viewType === "incoming" ? "Buchungsanfragen" : "Meine Buchungen"}</h1>
      <TableBookings roomReservation={reservations} getActionBtn={getActionBtn} showGuestName={props.viewType === 'incoming' ? true : false}/>
      {(dialogType != null && selectedItem) && (
      <DialogBookingComment viewType={props.viewType} onClose={onDialogClose} roomReservation={selectedItem} buttons={dialogType?.buttons} textfields={dialogType.textfields}
        caption={dialogType.caption} open={isDialogOpen}/>
      )}
    </ProgressContainer>

  );
};

export default PageMyBookings;
